import React, { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from './ui/select';
import { calculateBorrowingCapacity, getBorrowingInsights, BorrowingCapacityResult } from 'utils/mortgage-calculator/borrowingCalculations';
import { buildClassName } from 'utils/build-class-name';
import cardStyles from './ui/card.module.scss';
import styles from './BorrowingCapacityCalculator.module.scss';

const BorrowingCapacityCalculator = () => {
  const [paymentAmount, setPaymentAmount] = useState<number>(3000);
  const [frequency, setFrequency] = useState<'monthly' | 'biweekly' | 'weekly'>('monthly');
  const [interestRate, setInterestRate] = useState<number>(5.25);
  const [amortization, setAmortization] = useState<number>(25);
  const [result, setResult] = useState<BorrowingCapacityResult | null>(null);
  const [insights, setInsights] = useState<string[]>([]);

  const handleCalculate = () => {
    if (!paymentAmount || paymentAmount <= 0) return;
    if (!interestRate || interestRate <= 0 || interestRate > 20) return;
    if (!amortization || amortization < 1 || amortization > 30) return;

    const capacity = calculateBorrowingCapacity(
      paymentAmount,
      interestRate,
      amortization,
      frequency
    );

    setResult(capacity);
    const newInsights = getBorrowingInsights(
      capacity.mortgageAmount,
      paymentAmount,
      interestRate
    );
    setInsights(newInsights);
  };

  return (
    <div className={styles.component}>
      <Card className={buildClassName(cardStyles.shadowElevated, styles.card)}>
        <CardHeader className={styles.header}>
          <CardTitle className={styles.title}>
            <span className={styles.icon}>🧮</span>
            Borrowing Capacity Calculator
          </CardTitle>
          <p className={styles.subtitle}>
            Calculate how much you can borrow based on your payment capacity
          </p>
        </CardHeader>
        <CardContent className={styles.content}>
          <div className={styles.inputGrid}>
            <div className={styles.inputGroup}>
              <Label htmlFor="payment">Payment Amount ($)</Label>
              <Input
                id="payment"
                type="number"
                value={paymentAmount}
                onChange={e => setPaymentAmount(Number(e.target.value))}
                placeholder="3000"
                min="0"
              />
            </div>

            <div className={styles.inputGroup}>
              <Label htmlFor="frequency">Payment Frequency</Label>
              <Select value={frequency} onValueChange={(value: string) => setFrequency(value as 'monthly' | 'biweekly' | 'weekly')}>
                <SelectTrigger id="frequency">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="monthly">Monthly</SelectItem>
                  <SelectItem value="biweekly">Bi-weekly</SelectItem>
                  <SelectItem value="weekly">Weekly</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className={styles.inputGroup}>
              <Label htmlFor="rate">Interest Rate (%)</Label>
              <Input
                id="rate"
                type="number"
                step="0.1"
                value={interestRate}
                onChange={e => setInterestRate(Number(e.target.value))}
                placeholder="5.25"
                min="0"
                max="20"
              />
            </div>

            <div className={styles.inputGroup}>
              <Label htmlFor="amort">Amortization (years)</Label>
              <Input
                id="amort"
                type="number"
                value={amortization}
                onChange={e => setAmortization(Number(e.target.value))}
                placeholder="25"
                min="1"
                max="30"
              />
            </div>
          </div>

          <Button onClick={handleCalculate} className={styles.button} size="lg">
            Calculate Borrowing Capacity
          </Button>

          {result && (
            <div className={styles.resultCard}>
              <div className={styles.resultHeader}>
                <div className={styles.resultLabel}>Maximum Mortgage Amount</div>
                <div className={styles.resultAmount}>
                  ${result.mortgageAmount.toLocaleString()}
                </div>
                <div className={styles.resultSubtext}>
                  Estimated home price: ${(result.mortgageAmount / 0.8).toLocaleString()} (with 20% down)
                </div>
              </div>

              <div className={styles.resultGrid}>
                <div className={styles.resultItem}>
                  <div className={styles.resultItemLabel}>Payment Amount</div>
                  <div className={styles.resultItemValue}>${paymentAmount.toLocaleString()}</div>
                </div>
                <div className={styles.resultItem}>
                  <div className={styles.resultItemLabel}>Total Payments</div>
                  <div className={styles.resultItemValue}>{result.totalPayments}</div>
                </div>
                <div className={styles.resultItem}>
                  <div className={styles.resultItemLabel}>Total Interest Paid</div>
                  <div className={styles.resultItemValue}>${result.totalInterest.toLocaleString()}</div>
                </div>
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Smart Insights Panel */}
      {insights.length > 0 && (
        <Card className={buildClassName(cardStyles.shadowElevated, styles.insightsCard)}>
          <CardHeader className={styles.insightsHeader}>
            <CardTitle className={styles.insightsTitle}>
              <span className={styles.insightsIcon}>✨</span>
              Smart Insights
            </CardTitle>
          </CardHeader>
          <CardContent className={styles.insightsContent}>
            {insights.map((insight, index) => (
              <div
                key={index}
                className={styles.insightItem}
              >
                <p className={styles.insightText}>{insight}</p>
              </div>
            ))}
          </CardContent>
        </Card>
      )}
    </div>
  );
};

export default BorrowingCapacityCalculator;
